// Number-only build: the curve maths is all scalar, so the matrix/BigNumber/
// Complex machinery in the default entrypoint is dead weight.
import { evaluate } from "mathjs/number";
import type { NodeDefinition } from "~/nodes/types";

export const mathNode: NodeDefinition = {
  type: "math",
  label: "Math",
  category: "Math",
  width: 260,
  inputs: [
    {
      key: "a",
      label: "Arg A",
      socket: "number",
      control: { kind: "number", key: "a", label: "a", step: 0.1 },
    },
    {
      key: "b",
      label: "Arg B",
      socket: "number",
      control: { kind: "number", key: "b", label: "b", step: 0.1 },
    },
    {
      key: "c",
      label: "Arg C",
      socket: "number",
      control: { kind: "number", key: "c", label: "c", step: 0.1 },
    },
  ],
  outputs: [{ key: "result", label: "Result", socket: "number" }],
  controls: [
    { kind: "preview", key: "preview", label: "result" },
    { kind: "text", key: "fc", label: "expression" },
  ],
  // `round(a + b) * c` in math.js syntax — the old default used `Math.round`,
  // which math.js cannot parse, so the node always evaluated to 0.
  defaults: { a: 1, b: 2, c: 3, fc: "round(a + b) * c" },

  compute(ctx) {
    const scope = {
      a: ctx.number("a", 1),
      b: ctx.number("b", 2),
      c: ctx.number("c", 3),
    };

    let result: number;
    try {
      const value: unknown = evaluate(String(ctx.data["fc"]), scope);
      if (typeof value !== "number" || !Number.isFinite(value))
        throw new Error("Not a finite number");
      result = value;
    } catch (error) {
      result = 0;
      ctx.fail(
        "fc",
        error instanceof Error ? error.message : "Invalid expression",
      );
    }

    ctx.preview("preview", result);
    return { result };
  },
};
